// EPISTEME — IndexedDB persistence + cross-tab sync (ethical, robust, local-first)

import type { Audit, EncryptedRecord, LibraryEntry, StoredAudit } from './types';

const DB_NAME = 'episteme-db';
const DB_VERSION = 2;
const STORE_AUDITS = 'audits';
const STORE_META = 'meta';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_AUDITS)) {
        const store = db.createObjectStore(STORE_AUDITS, { keyPath: 'id' });
        store.createIndex('updated', 'updated', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

export async function saveAudit(audit: Audit): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_AUDITS, 'readwrite');
    tx.objectStore(STORE_AUDITS).put(audit);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function saveEncryptedRecord(rec: EncryptedRecord): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_AUDITS, 'readwrite');
    tx.objectStore(STORE_AUDITS).put(rec);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadAudit(id: string): Promise<StoredAudit | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_AUDITS, 'readonly');
    const req = tx.objectStore(STORE_AUDITS).get(id);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function listAudits(): Promise<LibraryEntry[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_AUDITS, 'readonly');
    const req = tx.objectStore(STORE_AUDITS).getAll();
    req.onsuccess = () => {
      const items: StoredAudit[] = req.result || [];
      const entries: LibraryEntry[] = items.map((item: any) => {
        if (item && item.isEncrypted) {
          // Use header so we can list locked audits without passphrase
          const h = item.header;
          return {
            id: item.id,
            domain: h.domain,
            scenario: '(encrypted)',
            claim: h.claim,
            updated: h.updated,
            hasDecision: h.hasDecision,
            processScore: h.processScore,
            currentCredence: h.currentCredence,
            isEncrypted: true,
          } as any;
        }
        const a = item as Audit;
        return {
          id: a.id,
          domain: a.domain,
          scenario: a.scenario,
          claim: a.claim,
          updated: a.updated,
          hasDecision: !!a.decision,
          processScore: computeQuickProcess(a),
          currentCredence: a.uncertainty.credence,
          isEncrypted: false,
        };
      }).sort((x, y) => y.updated - x.updated);
      resolve(entries);
    };
    req.onerror = () => reject(req.error);
  });
}

function computeQuickProcess(a: Audit): number {
  // lightweight version for list view
  let s = 40 + Math.min(30, a.evidence.length * 4);
  if (a.evidence.some(e => e.support === 'con')) s += 12;
  if (a.uncertainty.alternatives.length >= 2) s += 8;
  if (a.decision) s += 10;
  return Math.min(100, Math.max(0, Math.round(s)));
}

export async function deleteAudit(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_AUDITS, 'readwrite');
    tx.objectStore(STORE_AUDITS).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function exportAll(): Promise<string> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_AUDITS, 'readonly');
    const req = tx.objectStore(STORE_AUDITS).getAll();
    req.onsuccess = () => {
      const data = {
        exportedAt: new Date().toISOString(),
        version: 1,
        audits: req.result,
      };
      resolve(JSON.stringify(data, null, 2));
    };
    req.onerror = () => reject(req.error);
  });
}

export async function importAudits(json: string): Promise<number> {
  const parsed = JSON.parse(json);
  const audits: Audit[] = parsed.audits || [];
  const db = await openDB();
  let count = 0;
  const tx = db.transaction(STORE_AUDITS, 'readwrite');
  const store = tx.objectStore(STORE_AUDITS);
  for (const a of audits) {
    store.put(a);
    count++;
  }
  return new Promise((res, rej) => {
    tx.oncomplete = () => res(count);
    tx.onerror = () => rej(tx.error);
  });
}

// ---------- Cross-tab sync ----------
const channel = new BroadcastChannel('episteme-sync');

export function broadcastAuditUpdate(auditId: string, type: 'updated' | 'deleted' = 'updated') {
  try {
    channel.postMessage({ type: 'audit', action: type, id: auditId, ts: Date.now() });
  } catch {}
}

export function onAuditSync(cb: (msg: any) => void) {
  const handler = (ev: MessageEvent) => cb(ev.data);
  channel.addEventListener('message', handler);
  return () => channel.removeEventListener('message', handler);
}

// Web Locks for safe concurrent writes (when multiple tabs)
export async function withAuditLock<T>(key: string, fn: () => Promise<T>): Promise<T> {
  if (!('locks' in navigator)) return fn(); // graceful fallback
  return (navigator as any).locks.request(`episteme-${key}`, { mode: 'exclusive' }, fn);
}

// Simple meta (last opened etc.)
export async function setMeta(key: string, value: any) {
  const db = await openDB();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_META, 'readwrite');
    tx.objectStore(STORE_META).put({ key, value });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getMeta<T>(key: string): Promise<T | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_META, 'readonly');
    const req = tx.objectStore(STORE_META).get(key);
    req.onsuccess = () => resolve(req.result ? req.result.value : null);
    req.onerror = () => reject(req.error);
  });
}
